(() => {
  // New uploads do not need generated files or a filename-map entry. Measure a
  // small CDN preview, then position the ORIGINAL canvas (including shadows).
  // Versioned CDN URLs invalidate replacements; bounded storage avoids growth.
  const analysis = window.mwyProductSubjectAnalysis ||= new Map();
  const fullBounds = {width:1, height:1, x:0, y:0};
  function validBounds(value) {
    return value && ['width','height','x','y'].every(key => Number.isFinite(value[key])) &&
      value.width > 0 && value.width <= 1 && value.height > 0 && value.height <= 1 &&
      Math.abs(value.x) <= .5 && Math.abs(value.y) <= .5 &&
      (!('visualWidth' in value) || (
        ['visualWidth','visualHeight','visualX','visualY'].every(key => Number.isFinite(value[key])) &&
        value.visualWidth > 0 && value.visualWidth <= 1 && value.visualHeight > 0 && value.visualHeight <= 1 &&
        Math.abs(value.visualX) <= .5 && Math.abs(value.visualY) <= .5
      ));
  }
  function measurePixels(data, width, height) {
    // Older product composites were exported on white instead of transparent
    // canvas. Only trim a uniform white perimeter on landscape composites;
    // square cover art and photographs retain their intentional composition.
    let opaque = true;
    for (let i = 3; i < data.length; i += 4) if (data[i] < 240) { opaque = false; break; }
    if (opaque && width / height > 1.15) {
      let perimeter = 0, white = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (x !== 0 && y !== 0 && x !== width-1 && y !== height-1) continue;
        const i = (y * width + x) * 4;
        perimeter++; if (Math.min(data[i], data[i+1], data[i+2]) >= 248) white++;
      }
      if (white / perimeter >= .98) {
        const mask = new Uint8ClampedArray(data);
        for (let i = 0; i < mask.length; i += 4) {
          const difference = 255 - Math.min(data[i], data[i+1], data[i+2]);
          mask[i+3] = difference <= 3 ? 0 : difference >= 28 ? 255 : Math.round(difference * 8);
        }
        const bounds = measurePixels(mask, width, height);
        if (bounds.width < .96 && bounds.height < .96) return {...bounds, opaqueMargin:true};
      }
    }
    const columns = new Uint32Array(width), rows = new Uint32Array(height);
    const visualColumns = new Uint32Array(width), visualRows = new Uint32Array(height);
    let transparent = 0, solid = 0;
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha < 16) transparent++;
      if (alpha > 0) { visualColumns[x]++; visualRows[y]++; }
      // Size from near-opaque product pixels. Softer cast shadows remain part
      // of the visible bounds below, but never reduce the product itself.
      if (alpha >= 240) { columns[x]++; rows[y]++; solid++; }
    }
    // Opaque artwork and empty/near-empty files retain their original framing.
    if (transparent < width * height * .01 || solid < width * height * .002) return fullBounds;
    // Ignore isolated alpha specks without discarding detached product pieces.
    const minColumn = Math.max(2, Math.ceil(height * .005));
    const minRow = Math.max(2, Math.ceil(width * .005));
    let left = 0, right = width - 1, top = 0, bottom = height - 1;
    while (left < width && columns[left] < minColumn) left++;
    while (right >= left && columns[right] < minColumn) right--;
    while (top < height && rows[top] < minRow) top++;
    while (bottom >= top && rows[bottom] < minRow) bottom--;
    if (right < left || bottom < top) return fullBounds;
    // One sampling pixel of tolerance protects antialiased product edges.
    left = Math.max(0, left - 1); right = Math.min(width - 1, right + 1);
    top = Math.max(0, top - 1); bottom = Math.min(height - 1, bottom + 1);
    let visualLeft = 0, visualRight = width - 1, visualTop = 0, visualBottom = height - 1;
    // Even a faint shadow tail needs paint space; only solid-subject bounds
    // should discard isolated pixels. These bounds never determine scale.
    while (visualLeft < width && visualColumns[visualLeft] === 0) visualLeft++;
    while (visualRight >= visualLeft && visualColumns[visualRight] === 0) visualRight--;
    while (visualTop < height && visualRows[visualTop] === 0) visualTop++;
    while (visualBottom >= visualTop && visualRows[visualBottom] === 0) visualBottom--;
    if (visualRight < visualLeft || visualBottom < visualTop) {
      visualLeft = left; visualRight = right; visualTop = top; visualBottom = bottom;
    } else {
      visualLeft = Math.max(0, visualLeft - 1); visualRight = Math.min(width - 1, visualRight + 1);
      visualTop = Math.max(0, visualTop - 1); visualBottom = Math.min(height - 1, visualBottom + 1);
    }
    return {width:(right-left+1)/width, height:(bottom-top+1)/height,
      x:(left+right+1)/2/width-.5, y:(top+bottom+1)/2/height-.5,
      visualWidth:(visualRight-visualLeft+1)/width, visualHeight:(visualBottom-visualTop+1)/height,
      visualX:(visualLeft+visualRight+1)/2/width-.5, visualY:(visualTop+visualBottom+1)/2/height-.5};
  }
  function analyze(source) {
    if (analysis.has(source)) return analysis.get(source);
    const storageKey = 'mwy-subject-bounds-v5';
    let cache = [];
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) cache = stored.filter(entry => Array.isArray(entry) && typeof entry[0] === 'string' && validBounds(entry[1])).slice(-79);
    } catch (_) { /* Storage may be unavailable in private browsing. */ }
    const cached = cache.find(entry => entry[0] === source);
    const promise = cached ? Promise.resolve(cached[1]) : new Promise(resolve => {
      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      const timeout = setTimeout(() => finish(null), 10000);
      let finished = false;
      function finish(bounds) {
        if (finished) return;
        finished = true; clearTimeout(timeout);
        sample.onload = sample.onerror = null;
        if (bounds) {
          try {
            // Re-read to retain entries completed by other images concurrently.
            const current = JSON.parse(localStorage.getItem(storageKey) || '[]');
            const entries = Array.isArray(current) ? current.filter(entry => Array.isArray(entry) && entry[0] !== source && validBounds(entry[1])).slice(-79) : [];
            localStorage.setItem(storageKey, JSON.stringify([...entries, [source, bounds]]));
          } catch (_) {}
        }
        resolve(bounds || fullBounds); // Never hide images or block scrolling.
      }
      sample.onerror = () => finish(null);
      sample.onload = () => {
        try {
          const scale = Math.min(1, 384 / Math.max(sample.naturalWidth, sample.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(sample.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(sample.naturalHeight * scale));
          const context = canvas.getContext('2d', {willReadFrequently:true});
          context.drawImage(sample, 0, 0, canvas.width, canvas.height);
          finish(measurePixels(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height));
          canvas.width = canvas.height = 0;
        } catch (_) { finish(null); }
      };
      sample.src = source;
    });
    analysis.set(source, promise);
    if (analysis.size > 100) analysis.delete(analysis.keys().next().value);
    return promise;
  }
  window.mwyMeasureProductSubject = analyze;
  window.mwyApplyProductPaintBounds = (img, bounds) => {
    // Transparent shadows keep their complete canvas. White-canvas composites
    // retain their shadow envelope, but their blank exterior cannot cover UI.
    if (!bounds.opaqueMargin) { img.style.clipPath = ''; return; }
    const w = bounds.visualWidth, h = bounds.visualHeight;
    const x = bounds.visualX, y = bounds.visualY;
    img.style.clipPath = `inset(${Math.max(0,.5+y-h/2)*100}% ${Math.max(0,.5-x-w/2)*100}% ${Math.max(0,.5-y-h/2)*100}% ${Math.max(0,.5+x-w/2)*100}%)`;
  };

  // Product tiles use the same measurements as the full product gallery.
  // Only near-viewport cards request previews; dynamically inserted collection
  // results and theme-editor sections are discovered without polling.
  const cards = new Map();
  let frame = 0;
  function schedule() { if (!frame) frame = requestAnimationFrame(update); }
  function update() {
    frame = 0;
    cards.forEach((state, card) => {
      if (!card.isConnected) {
        resize.unobserve(state.box); intersection.unobserve(card);
        state.images.forEach(item => item.img.removeEventListener('load', schedule));
        cards.delete(card); return;
      }
      const width = state.box.clientWidth, height = state.box.clientHeight;
      if (!width || !height) return;
      const fill = Math.max(60, Math.min(100, Number(card.dataset.framingFill) || 90)) / 100;
      state.images.forEach(({img, bounds}) => {
        if (!bounds || !img.naturalWidth) return;
        const scale = Math.min(width * fill / (img.naturalWidth * bounds.width), height * fill / (img.naturalHeight * bounds.height));
        const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
        const top = card.dataset.framingAlignment === 'center'
          ? (height-h)/2 - bounds.y*h
          : -(0.5 + bounds.y - bounds.height/2)*h;
        const geometry = {width:w, height:h, left:(width-w)/2-bounds.x*w, top};
        Object.entries(geometry).forEach(([key, value]) => img.style.setProperty('--mwy-frame-'+key, value+'px'));
        if (img === state.images[0].img) {
          const subjectLeft = geometry.left + w * (.5 + bounds.x - bounds.width / 2);
          const subjectTop = geometry.top + h * (.5 + bounds.y - bounds.height / 2);
          const visualBottom = geometry.top + h * (.5 + (bounds.visualY ?? bounds.y) + (bounds.visualHeight ?? bounds.height) / 2);
          const wrapper = card.closest('.mwy-quick-add-media');
          if (wrapper) {
            wrapper.style.setProperty('--mwy-grid-subject-center-x', subjectLeft + w * bounds.width / 2 + 'px');
            wrapper.style.setProperty('--mwy-grid-subject-center-y', subjectTop + h * bounds.height / 2 + 'px');
            wrapper.style.setProperty('--mwy-grid-subject-bottom', subjectTop + h * bounds.height + 'px');
            // Retain one quarter of the original empty space below visible paint.
            wrapper.style.setProperty('--mwy-grid-details-lift', Math.max(0, (height - visualBottom) * .75) + 'px');
            wrapper.classList.add('mwy-grid-framing-ready');
          }
        }
        window.mwyApplyProductPaintBounds(img, bounds);
        img.classList.add('mwy-grid-framed-image');
        card.classList.add('mwy-grid-framed');
        if (img === state.images[0].img && card.dataset.mwyPrimaryFramed !== 'true') {
          card.dataset.mwyPrimaryFramed = 'true';
          card.dispatchEvent(new CustomEvent('mwy:grid-framed', {bubbles:true}));
        }
      });
    });
  }
  const resize = new ResizeObserver(schedule);
  const intersection = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      intersection.unobserve(entry.target);
      const state = cards.get(entry.target);
      if (!state) return;
      state.images.forEach(item => analyze(item.source).then(bounds => {
        item.bounds = bounds; schedule();
      }));
    });
  // Prepare several rows before they enter view. This keeps lazy loading, but
  // prevents a card's entrance from winning the race against its framing pass.
  }, {rootMargin:'1200px 0px'});
  function discover(root) {
    const selector = '[data-mwy-grid-framing="true"]';
    const found = [...(root.querySelectorAll ? root.querySelectorAll(selector) : [])];
    if (root.matches && root.matches(selector)) found.push(root);
    found.forEach(card => {
      if (cards.has(card)) return;
      const box = card.querySelector('.grid__image-ratio, .image-wrap');
      if (!box) return;
      const primary = box.querySelector('img');
      const secondary = card.querySelector('.grid-product__secondary-image img');
      const images = [{img:primary, source:card.dataset.framingPrimary}, {img:secondary, source:card.dataset.framingSecondary}].filter(item => item.img && item.source);
      if (!images.length) return;
      cards.set(card, {box, images});
      images.forEach(item => item.img.addEventListener('load', schedule));
      resize.observe(box); intersection.observe(card);
    });
  }
  function init() {
    discover(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) discover(node); }));
      if (records.some(record => record.removedNodes.length)) schedule();
    }).observe(document.body, {childList:true, subtree:true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();
